import { alert, Button, Dialog, Text } from '@weareredlight/design-system'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import type { RequiredProviderType } from 'schemas/appointment'
import type { AppointmentInputType } from 'types/appointments'
import type { Certification } from 'utils/enums'

import AppointmentForm from './AppointmentForm'

import api from 'api/api'
import { ApptFields } from 'components/Appointments/utils'
import EntityDetail from 'components/EntityDetail'
import { ProceduresFields } from 'components/Procedures/utils'
import { useFormManager } from 'hooks/useFormManager'
import { useSchedule } from 'pages/Schedule/ScheduleAppointment'
import { createAppointmentInputSchema } from 'schemas/appointment'
import { getName } from 'utils/text'

const AppointmentEdit = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const {
    draggedEvent,
    activeAppointment,
    reloadAppointmentsList,
    resetActiveProcedure,
    resources,
    isLoadingResources,
    updateTreatment,
    setResourcesSelected,
  } = useSchedule()

  const requiredProviderTypes = useMemo<RequiredProviderType[]>(() => {
    const requiredProviders =
      activeAppointment?.treatmentProcedures?.[0].procedure?.[
        ProceduresFields.REQUIRED_PROVIDERS
      ] ?? []

    return requiredProviders
      .filter(
        (
          p,
        ): p is (typeof requiredProviders)[number] & {
          certification: Certification
        } => !!p.certification,
      )
      .map(p => ({
        certification: p.certification,
        count: p.count ?? 1,
        specialtyValue: p.providerSpeciality?.value,
      }))
  }, [activeAppointment?.treatmentProcedures])

  const allProviders = useMemo(
    () => resources?.providers ?? [],
    [resources?.providers],
  )

  const schema = useMemo(
    () => createAppointmentInputSchema(requiredProviderTypes, allProviders),
    [requiredProviderTypes, allProviders],
  )

  const [dialogOpen, setDialogOpen] = useState(false)

  const handleRemoveConfirm = async () => {
    if (!activeAppointment) return
    setDialogOpen(false)

    try {
      await api.cancelAppointment(activeAppointment.id)
      reloadAppointmentsList()
      navigate('', { replace: true })
      alert.success(
        `${t('Success')}!`,
        `${t('Appointment cancelled successfully.')}`,
        {
          position: 'bottom-right',
        },
      )
    } catch (error) {
      alert.error(`${t('Error')}!`, `${t('Failed to cancel the appointment.')}`)
    }
  }

  const form = useFormManager<AppointmentInputType>({
    defaultValues: {
      [ApptFields.START_TIME]: activeAppointment?.startTime,
      [ApptFields.END_TIME]: activeAppointment?.endTime,
      [ApptFields.DURATION_BEFORE]: activeAppointment?.durationBefore,
      [ApptFields.TREATMENT_PROCEDURE_ID]:
        activeAppointment?.treatmentProcedures?.[0].id,
      [ApptFields.ROOM_ID]: activeAppointment?.room?.id,
      [ApptFields.PROVIDER_IDS]: activeAppointment?.providers?.map(
        ({ id }) => id,
      ),
    },
    enableReinitialize: true,
    schema,
    onCancel: () => {
      resetActiveProcedure()
      navigate('', { replace: true })
    },
    onSubmit: async (input: AppointmentInputType) => {
      if (!activeAppointment) return
      await api.updateAppointment({ id: activeAppointment.id, input })
      resetActiveProcedure()
      navigate('', { replace: true })
      updateTreatment()
      alert.success(
        `${t('Success')}!`,
        `${t('Appointment edited successfully.')}`,
        {
          position: 'bottom-right',
        },
      )
    },
  })

  const removeButton = activeAppointment?.id ? (
    <Button
      variant="danger"
      disabled={form.formState.isSubmitting}
      onClick={() => setDialogOpen(true)}
    >
      {t('Cancel Appointment')}
    </Button>
  ) : (
    <></>
  )

  if (!activeAppointment) return null

  return (
    <>
      <Text variant="h2">
        {t('Edit appointment')}
        <Text color="neutral800">
          {' '}
          - {t('for')}{' '}
          <b>
            {activeAppointment?.treatmentProcedures
              ?.map(tp => tp?.procedure?.name)
              .filter(Boolean)
              .join(', ')}
          </b>
        </Text>
      </Text>
      <EntityDetail label={t('Patient')}>
        {getName(
          activeAppointment.treatmentProcedures?.[0].treatment,
          'patient',
        )}
      </EntityDetail>
      <AppointmentForm
        {...form}
        procedure={activeAppointment.treatmentProcedures?.[0].procedure}
        treatmentProcedures={activeAppointment.treatmentProcedures}
        event={draggedEvent}
        resources={resources}
        isLoading={isLoadingResources}
        setResourcesSelected={setResourcesSelected}
        additionalActions={removeButton}
      />
      <Dialog
        open={dialogOpen}
        onConfirm={handleRemoveConfirm}
        closeFn={() => setDialogOpen(false)}
        title={t('Are you sure you want to cancel this appointment?')}
        description={t('This action cannot be undone.')}
        variant="danger"
        confirmButtonText={t('Confirm')}
        cancelButtonText={t('Cancel')}
      />
    </>
  )
}

export default AppointmentEdit
